import { Check, X } from "lucide-react";
import { useMemo } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ACCESS_AREAS, areaModules } from "../../constants/accessAreas.constants";
import type { GatheredStatement } from "../../types/access.types";
import { resolveCell } from "../../utils/denyFirst";

export interface EffectiveMatrixProps {
  /** The principal's gathered statements (roles → policies → statements). */
  statements: readonly GatheredStatement[];
  className?: string;
}

/**
 * The resulting access, area by area. Only modules with something allowed or denied get a row; the rest of an
 * area is counted, and areas with nothing at all are named in one closing line. Listing all 31 modules every
 * time — mostly empty for an ordinary role — was most of the scroll in both drawers, and buried the few rows
 * that said anything.
 *
 * Every cell is resolved deny-first. A `*` marks a deny that overrode an allow.
 */
export function EffectiveMatrix({ statements, className }: EffectiveMatrixProps) {
  const { t } = useTranslations("settings");

  /**
   * Resolve all cells once per statement set instead of on every render: `resolveCell` scans the whole
   * statement array per cell, and the matrix sits inside drawers whose local state re-renders on every
   * keystroke. The parents already memoize `statements`, so keying on it is enough.
   */
  const areas = useMemo(
    () =>
      ACCESS_AREAS.map((area) => {
        const rows = areaModules(area).map((mod) => {
          const cells = mod.actions.map((atom) => ({ atom, res: resolveCell(statements, mod.id, atom) }));
          return { mod, cells, active: cells.some((c) => c.res.effect !== "none") };
        });
        return {
          area,
          active: rows.filter((r) => r.active),
          idle: rows.length - rows.filter((r) => r.active).length,
        };
      }),
    [statements],
  );

  const withAccess = areas.filter((a) => a.active.length > 0);
  const withoutAccess = areas.filter((a) => a.active.length === 0);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {withAccess.map(({ area, active, idle }) => {
        const AreaIcon = area.icon;
        return (
          <section key={area.id} aria-label={t(area.labelKey)}>
            <h4 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
              <AreaIcon size={12} className="text-muted-foreground" aria-hidden />
              {t(area.labelKey)}
            </h4>
            <div className="flex flex-col gap-1.5 pl-[18px]">
              {active.map(({ mod, cells }) => (
                <div key={mod.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className="min-w-0 flex-1 basis-36 truncate text-[11.5px] text-foreground"
                    title={mod.id}
                  >
                    {t(mod.labelKey)}
                  </span>
                  <span className="flex shrink-0 gap-1">
                    {cells.map(({ atom, res }) => {
                      const denyWins = res.effect === "deny" && res.overridden.length > 0;
                      return (
                        <span
                          key={atom}
                          title={`${mod.id}:${atom} → ${res.effect}${denyWins ? " (deny > allow)" : ""}`}
                          className={cn(
                            "inline-flex min-w-[3.75rem] items-center justify-center gap-1 rounded-sm border px-1.5 py-px text-[10.5px]",
                            res.effect === "allow" && "border-primary/30 bg-primary/10 text-primary",
                            res.effect === "deny" &&
                              "border-destructive/40 bg-destructive/10 text-destructive",
                            res.effect === "none" && "border-border text-muted-foreground/50",
                          )}
                        >
                          {res.effect === "allow" && <Check size={9} aria-hidden />}
                          {res.effect === "deny" && <X size={9} aria-hidden />}
                          {t(`access_level_${atom}`)}
                          {denyWins ? "*" : ""}
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))}
              {idle > 0 && (
                <span className="text-[11px] text-muted-foreground/70">
                  {idle === 1 ? t("access_effective_idle_one") : t("access_effective_idle", { count: idle })}
                </span>
              )}
            </div>
          </section>
        );
      })}

      {withoutAccess.length > 0 && (
        <p className="border-t border-dashed border-border pt-2 text-[11.5px] text-muted-foreground">
          {t("access_effective_no_access", {
            areas: withoutAccess.map(({ area }) => t(area.labelKey)).join(", "),
          })}
        </p>
      )}
    </div>
  );
}
